import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { FormViewer, FormViewerHandle } from '@wix/form-viewer/widget';
import {
  FormError,
  FormStatus as WixFormStatus,
  FormValues,
  FORM_STATUS,
} from '@wix/form-viewer';
import { classes, st } from './WixForm.st.css';
import { useSettingsAdapter } from '../../Settings/Utils/SettingsAdapter';
import settingsParams from '../../settingsParams';
import { WixForms } from '../../../../utils/state/types';
import { useFormActions } from '../../Hooks/useFormActions';
import { FormStatus } from '../../../../types/form-state';
import { WixFormDatahooks } from './datahooks';

export interface WixFormProps {
  formId: string;
  wixForms: WixForms;
  formValues: FormValues;
  handleFormChange: (values: FormValues) => void;
  wixFormRef: React.RefObject<FormViewerHandle>;
  // When several forms are mounted (multi-participant flow) the parent aggregates their
  // busy state instead of each form toggling the global form status on its own.
  onBusyChange?: (isBusy: boolean) => void;
  // Move keyboard focus to this form (e.g. the participant section that just opened).
  autoFocus?: boolean;
}
const WixForm: React.FC<WixFormProps> = ({
  formId,
  formValues,
  handleFormChange,
  wixFormRef,
  wixForms,
  onBusyChange,
  autoFocus = false,
}) => {
  const bi = useBi();
  const { setFormStatus } = useFormActions();
  const { i18n } = useTranslation();
  const [formErrors, setFormErrors] = useState<FormError[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useEnvironment();

  useEffect(() => {
    if (autoFocus) {
      rootRef.current?.focus();
    }
  }, [autoFocus]);

  const handleFormValidate = useCallback((errors: FormError[]) => {
    setFormErrors(errors);
  }, []);

  const handleStatusChanged = useCallback(
    (status: WixFormStatus) => {
      const isBusy = status === FORM_STATUS.BUSY;
      if (onBusyChange) {
        onBusyChange(isBusy);
        return;
      }
      setFormStatus(isBusy ? FormStatus.GENERAL_PROCESS : FormStatus.IDLE);
    },
    [onBusyChange, setFormStatus],
  );

  const settings = useSettingsAdapter();
  const theme = settings.get(settingsParams.fieldsBorderStyle);

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      data-hook={WixFormDatahooks.Main}
      className={st(classes.root, { theme, isMobile })}
    >
      <FormViewer
        onStatusChange={(status) => handleStatusChanged(status)}
        onChange={handleFormChange}
        onValidate={handleFormValidate}
        values={formValues}
        errors={formErrors}
        i18n={i18n}
        formId={formId}
        bi={bi}
        ref={wixFormRef}
        overrides={wixForms.overrides}
      />
    </div>
  );
};

export default WixForm;
